import { CloseIcon } from '@chakra-ui/icons';
import { Box, Flex, Grid, IconButton, Text, Tooltip, useColorModeValue, VStack } from '@chakra-ui/react';
import { calibrationRecordRepository } from 'api/calibrationRecord';
import Card from 'components/card/Card';
import InputField from 'components/fields/InputField';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
type FormState = {
  calibrationId: string;
  orgId: string;
  locationId: string;
  assetId: string;
  vendorId: string;
  departmentId: string;
  assetEquipmentId: string;
  contactName: string;
  contactEmail: string;
  contactNumber: string;
  calibrationFrequency: string;
  calibrationDate: string;
  calibrationDueDate: string;
  installedDate: string;
  priority: string;
  daysToExpire: string;
  notes: string;
  noOfCertificates: string;
};

export default function CalibrationDetail() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const navigate = useNavigate();
  const location = useLocation();

  const search = new URLSearchParams(location.search);
  const viewId = search.get('id') ?? '';
  const { calibrationId } = useParams();
  const [form, setForm] = useState<FormState>({
    calibrationId: '',
    orgId: '',
    locationId: '',
    assetId: '',
    vendorId: '',
    departmentId: '',
    assetEquipmentId: '',
    contactName: '',
    contactEmail: '',
    contactNumber: '',
    calibrationFrequency: '',
    calibrationDate: '',
    calibrationDueDate: '',
    installedDate: '',
    priority: '',
    daysToExpire: '',
    notes: '',
    noOfCertificates: '',
  });

  const [headerContext, setHeaderContext] = useState<any>(null);

  const renderTextWithTooltip = (label: string, value: string | undefined, maxLength = 10) => {
    if (!value) value = '-';
    const isLong = value.length > maxLength;
    const displayValue = isLong ? value.slice(0, maxLength) + '...' : value;

    return (
      <Tooltip label={isLong ? value : undefined} hasArrow placement="top">
        <Text fontSize="sm" noOfLines={1} maxW="160px">
          {label}: {displayValue}
        </Text>
      </Tooltip>
    );
  };

  useEffect(() => {
    if (!calibrationId) return;

    const fetchRecord = async () => {
      try {
        const record = await calibrationRecordRepository.getRecordById(calibrationId);

        if (record) {
          setForm({
            calibrationId: record.calibrationId ?? '',
            orgId: record.orgId ?? '',
            locationId: record.locationId ?? '',
            assetId: record.assetId ?? '',
            vendorId: record.vendorId ?? '',
            departmentId: record.departmentId ?? '',
            assetEquipmentId: record.assetEquipmentId ?? '',
            contactName: record.contactName ?? '',
            contactEmail: record.contactEmail ?? '',
            contactNumber: record.contactNumber ?? '',
            calibrationFrequency: record.calibrationFrequency ?? '',
            calibrationDate: record.calibrationDate
              ? record.calibrationDate.split('T')[0]
              : '',
            calibrationDueDate: record.calibrationDueDate
              ? record.calibrationDueDate.split('T')[0]
              : '',
            installedDate: record.installedDate
              ? record.installedDate.split('T')[0]
              : '',
            priority: record.priority ?? '',
            daysToExpire:
              record.daysToExpire !== undefined && record.daysToExpire !== null
                ? record.daysToExpire.toString()
                : '',
            noOfCertificates: record.noOfCertificates !== undefined && record.noOfCertificates !== null
              ? record.noOfCertificates.toString()
              : '',
            notes: record.notes ?? '',
          });

          const header = await calibrationRecordRepository.getHeaderContext(
            record.orgId,
            record.locationId,
            record.assetId,
            record.vendorId,
            record.departmentId
          );

          setHeaderContext(header);
        }
      } catch (error) {
      }
    };

    fetchRecord();
  }, [calibrationId]);

  return (
    <Flex direction="column" height="100vh" overflow="hidden" align="center" justify="flex-start" position="relative">
      <Box
        position="absolute"
        top="90"
        left="0"
        w="100%"
        h="40vh"
        bgGradient="linear(to-b, brand.400, brand.600)"
        borderRadius="20px"
      />

      {/* Header Context */}
      <Box w={{ sm: '330px', md: '700px', lg: '900px' }} mt={{ md: '120px' }} mb="100px" zIndex={2}>
        <Grid templateColumns="repeat(5, 1fr)" gap="20px">
          <VStack p={4} borderWidth="1px" borderRadius="md" bg={useColorModeValue('white', 'gray.700')} shadow="md" align="start" h="150px" w="100%">
            <Text fontWeight="bold" w="100%" textAlign="center">Organization</Text>
            {renderTextWithTooltip('Name', headerContext?.orgInfo?.name)}
            {renderTextWithTooltip('Contact', headerContext?.orgInfo?.contact)}
            {renderTextWithTooltip('GSTIN', headerContext?.orgInfo?.gsTin)}
          </VStack>

          <VStack p={4} borderWidth="1px" borderRadius="md" bg={useColorModeValue('white', 'gray.700')} shadow="md" align="start" h="150px" w="100%">
            <Text fontWeight="bold" w="100%" textAlign="center">Plant</Text>
            {renderTextWithTooltip('Name', headerContext?.locationInfo?.name)}
            {renderTextWithTooltip('City', headerContext?.locationInfo?.city)}
            {renderTextWithTooltip('State', headerContext?.locationInfo?.state)}
          </VStack>

          <VStack p={4} borderWidth="1px" borderRadius="md" bg={useColorModeValue('white', 'gray.700')} shadow="md" align="start" h="150px" w="100%">
            <Text fontWeight="bold" w="100%" textAlign="center">Asset</Text>
            {renderTextWithTooltip('EQ.ID', headerContext?.assetInfo?.assetEquipmentId)}
            {renderTextWithTooltip('Type', headerContext?.assetInfo?.type)}
            {renderTextWithTooltip('Status', headerContext?.assetInfo?.status)}
          </VStack>

          <VStack p={4} borderWidth="1px" borderRadius="md" bg={useColorModeValue('white', 'gray.700')} shadow="md" align="start" h="150px" w="100%">
            <Text fontWeight="bold" w="100%" textAlign="center">Vendor</Text>
            {renderTextWithTooltip('Name', headerContext?.vendorInfo?.name)}
            {renderTextWithTooltip('Contact', headerContext?.vendorInfo?.contactNumber)}
            {renderTextWithTooltip('GSTIN', headerContext?.vendorInfo?.gsTin)}
          </VStack>

          <VStack p={4} borderWidth="1px" borderRadius="md" bg={useColorModeValue('white', 'gray.700')} shadow="md" align="start" h="150px" w="100%">
            <Text fontWeight="bold" w="100%" textAlign="center">Department</Text>
            {renderTextWithTooltip('Name', headerContext?.departmentInfo?.name)}
          </VStack>
        </Grid>
      </Box>

      {/* Detail Card */}
      <Card w={{ sm: '330px', md: '700px', lg: '900px' }} p="30px" mt={{ md: '20px' }} zIndex={1} overflowY="auto" borderRadius="16px" position="relative">
        <IconButton
          icon={<CloseIcon />}
          aria-label="Close"
          position="absolute"
          top="10px"
          right="10px"
          size="sm"
          onClick={() => navigate("/admin/asset-management/assetList", {
            state: location.state,
          })}
        />

        <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px" textAlign="left">
          Calibration Details
        </Text>

        <Grid templateColumns="repeat(2, 1fr)" gap={6}>
          <InputField label="Contact Name" value={form.contactName} isReadOnly />
          <InputField label="Contact Email" value={form.contactEmail} isReadOnly />
          <InputField label="Contact Number" value={form.contactNumber} isReadOnly />
          <InputField label="Priority" value={form.priority} isReadOnly />
          <InputField label="Calibration Date" type="date" value={form.calibrationDate} isReadOnly />
          <InputField label="Calibration Due Date" type="date" value={form.calibrationDueDate} isReadOnly />
          <InputField label="Calibration Frequency" value={form.calibrationFrequency} isReadOnly />
          {/* <InputField label="Priority" value={form.priority} isReadOnly /> */}
          <InputField label="Days To Expire" value={form.daysToExpire} isReadOnly />
          <InputField label="No of Certificates" value={form.noOfCertificates} isReadOnly />
        </Grid>
      </Card>
    </Flex>
  );
}
